import styles from './HoveredInfoIcon.module.scss';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as InfoIcon } from '../../../assets/svg/icons/info_point_pieno.svg';
import clx from 'classnames';

const LONG_TEXT_THRESHOLD = 120;
const VIEWPORT_GAP = 8;

const HoveredInfoIcon = ({
  title,
  description,
  sidePosition = 'left',
  wide,
}) => {
  const descriptionText =
    typeof description === 'string' ? description : '';
  const isWide =
    wide === true ||
    (wide !== false && descriptionText.length >= LONG_TEXT_THRESHOLD);

  const iconRef = useRef(null);
  const cardRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);

  const updatePosition = useCallback(() => {
    const icon = iconRef.current;
    const card = cardRef.current;
    if (!icon || !card) return;

    const rect = icon.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const preferUp = sidePosition === 'up';

    let placement = preferUp ? 'top' : 'bottom';
    let top = preferUp
      ? rect.top - cardRect.height - 8
      : rect.bottom + 8;

    if (!preferUp && top + cardRect.height > window.innerHeight - VIEWPORT_GAP) {
      placement = 'top';
      top = rect.top - cardRect.height - 8;
    } else if (preferUp && top < VIEWPORT_GAP) {
      placement = 'bottom';
      top = rect.bottom + 8;
    }

    let left =
      sidePosition === 'right'
        ? rect.left
        : rect.right - cardRect.width;

    left = Math.min(
      Math.max(VIEWPORT_GAP, left),
      window.innerWidth - cardRect.width - VIEWPORT_GAP,
    );
    top = Math.min(
      Math.max(VIEWPORT_GAP, top),
      window.innerHeight - cardRect.height - VIEWPORT_GAP,
    );

    setCoords({ top, left, placement });
  }, [sidePosition]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return undefined;
    }
    updatePosition();
    return undefined;
  }, [open, description, isWide, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onReposition = () => updatePosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, updatePosition]);

  const tooltip = open
    ? createPortal(
        <div
          ref={cardRef}
          className={clx(styles.info_card_portal, {
            [styles.wide]: isWide,
            [styles.placement_top]: coords?.placement === 'top',
            [styles.visible]: !!coords,
          })}
          style={
            coords
              ? { top: coords.top, left: coords.left }
              : { top: 0, left: 0 }
          }
          role="tooltip"
        >
          {description}
        </div>,
        document.body,
      )
    : null;

  return (
    <div
      className={styles.info_container}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <div className={styles.icon_value_container}>
        {title && <div className={styles.info_title}>{title}</div>}
        <div
          ref={iconRef}
          className={clx(styles.info_icon_container, {
            [styles.left_position]: sidePosition === 'left',
            [styles.right_position]: sidePosition === 'right',
            [styles.right_up_position]: sidePosition === 'up',
            [styles.wide]: isWide,
          })}
        >
          <InfoIcon className={styles.info_icon} />
        </div>
      </div>
      {tooltip}
    </div>
  );
};

export default HoveredInfoIcon;
